import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import TeacherExtraEntryCreateModal from './TeacherExtraEntryCreateModal';
import TeacherExtraEntryModal from './TeacherExtraEntryModal';

/**
 * Доп. занятия учителя — недельная сетка.
 *
 * Что показываем:
 *   • занятия, привязанные к учителю через `extra_lessons.teacher_id = me`;
 *   • статус: planned / conducted / canceled — отдельные стили.
 *
 * Что может учитель:
 *   • создать новое занятие из своих карточек `extra_lessons` (которые
 *     завёл админ — там предмет, тип individual/group, цены);
 *   • открыть занятие → добавить/убрать учеников, отметить «проведено»,
 *     отменить с причиной;
 *   • редактировать карточки занятий учитель НЕ может — это админская
 *     работа (цены, договорные ставки).
 *
 * Бэкенд проверяет права в `requireEntryAccess` / `requireLessonCardAccess`:
 * админу с правом `extra.schedule:*` всё доступно, учителю — только своё.
 */
const URL_SCHEMA = {
  week:   '',
  open:   '',  // id открытой деталки
  create: '',  // 'YYYY-MM-DD' с дефолтной датой при открытии мастера
};

const DAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function pad(n) { return String(n).padStart(2, '0'); }
function isoOf(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }
function parseIso(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map((x) => parseInt(x, 10));
  return new Date(y, m - 1, d);
}
function getMonday(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const wd = x.getDay() || 7;
  x.setDate(x.getDate() - (wd - 1));
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function shortTime(t) { return (t || '').slice(0, 5); }
function weekTitle(monday) {
  const sunday = addDays(monday, 6);
  const f = (d) => d.getDate() + ' ' + d.toLocaleDateString('ru-RU', { month: 'short' });
  const sameMonth = monday.getMonth() === sunday.getMonth();
  return sameMonth
    ? `${monday.getDate()}–${sunday.getDate()} ${monday.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}`
    : `${f(monday)} – ${f(sunday)} ${sunday.getFullYear()}`;
}

const STATUS_CLS = {
  planned:   'es-entry es-entry--planned',
  conducted: 'es-entry es-entry--conducted',
  canceled:  'es-entry es-entry--canceled',
};

export default function TeacherExtraSchedulePage() {
  const { tweaks, updateTweak } = useShell();
  const [params, setParams] = useUrlState(URL_SCHEMA);

  const monday = useMemo(() => {
    return params.week ? getMonday(parseIso(params.week)) : getMonday(new Date());
  }, [params.week]);

  const [data, setData]    = useState(null);
  const [loading, setLoad] = useState(false);
  const [error, setError]  = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const reload = () => setReloadTick((n) => n + 1);

  useEffect(() => {
    setLoad(true); setError(null);
    api.get('/api/teacher/extra-schedule?week=' + isoOf(monday))
      .then((res) => setData(res))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoad(false));
  }, [monday.getTime(), reloadTick]);

  const entries     = data?.entries     || [];
  const lessonCards = data?.lessonCards || [];
  const times       = data?.times       || [];
  const rooms       = data?.rooms       || [];

  // Группировка по (timeId, date)
  const byCell = useMemo(() => {
    const map = new Map();
    for (const r of entries) {
      const k = r.timeId + '|' + r.date;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }
    return map;
  }, [entries]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(monday, i)), [monday]);
  const todayIso = isoOf(new Date());

  const goToWeek  = (delta) => setParams({ week: isoOf(addDays(monday, delta * 7)) });
  const goToToday = () => setParams({ week: '' });

  // У учителя нет карточек → создать занятие нечем. Показываем подсказку.
  const noCards = !loading && lessonCards.length === 0;
  const canCreate = lessonCards.length > 0;

  return (
    <>
      <Topbar
        breadcrumb={['Доп. занятия', 'Расписание']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />

      <div className="filterbar">
        <div className="filterbar__row">
          <div className="es-weeknav">
            <button className="btn btn--sm" onClick={() => goToWeek(-1)} title="Предыдущая неделя">‹</button>
            <button className="btn btn--sm" onClick={goToToday}>Сегодня</button>
            <button className="btn btn--sm" onClick={() => goToWeek(1)} title="Следующая неделя">›</button>
            <span className="es-weeknav__title">{weekTitle(monday)}</span>
          </div>

          <div className="filterbar__spacer" />
          <span className="filterbar__meta">Занятий: <b>{entries.length}</b></span>
          <div className="filterbar__rightActions">
            <button
              className="btn btn--primary btn--sm"
              onClick={() => setParams({ create: todayIso })}
              disabled={!canCreate}
              title={canCreate ? undefined : 'Карточки доп. занятий ещё не заведены администратором'}
            >
              <Icon name="plus" size={13} /> Новое занятие
            </button>
          </div>
        </div>
      </div>

      <div className="main-content-wrap">
        {error && <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>}
        {noCards && !error && (
          <div className="ref-empty" style={{ padding: 32 }}>
            У вас пока нет карточек доп. занятий. Попросите администратора
            завести карточку «Доп. занятия» в разделе настройки и привязать
            её к вам — после этого можно расставлять занятия.
          </div>
        )}

        {!noCards && (
          <div className="es-grid">
            <div className="es-grid__head">
              <div className="es-grid__corner">{loading ? '…' : ''}</div>
              {days.map((d, i) => {
                const iso = isoOf(d);
                return (
                  <div
                    key={iso}
                    className={'es-grid__day' + (iso === todayIso ? ' is-today' : '')}
                  >
                    <span className="es-grid__day-name">{DAY_LABELS[i]}</span>
                    <span className="es-grid__day-date">{d.getDate()}.{pad(d.getMonth() + 1)}</span>
                  </div>
                );
              })}
            </div>

            {times.length === 0 && (
              <div className="ref-empty" style={{ padding: 32 }}>
                Не заведены временные интервалы для доп. занятий — попросите администратора.
              </div>
            )}

            {times.map((t) => (
              <div key={t.id} className="es-grid__row">
                <div className="es-grid__time mono">
                  <span>{shortTime(t.timeStart)}</span>
                  <span className="es-grid__time-sep">—</span>
                  <span>{shortTime(t.timeEnd)}</span>
                </div>
                {days.map((d) => {
                  const iso = isoOf(d);
                  const items = byCell.get(t.id + '|' + iso) || [];
                  return (
                    <div
                      key={iso}
                      className={'es-cell' + (iso === todayIso ? ' is-today' : '')}
                      onDoubleClick={() => canCreate && setParams({ create: iso })}
                      title={canCreate ? 'Двойной клик — добавить занятие' : undefined}
                    >
                      {items.map((e) => {
                        const statusIcon = e.status === 'conducted' ? 'check'
                                         : e.status === 'canceled'  ? 'x'
                                         : 'clock';
                        const statusTitle = e.status === 'conducted' ? 'Проведено'
                                          : e.status === 'canceled'  ? 'Отменено'
                                          : 'Запланировано (не проведено)';
                        return (
                          <button
                            key={e.id}
                            type="button"
                            className={STATUS_CLS[e.status] || 'es-entry'}
                            onClick={() => setParams({ open: String(e.id) })}
                          >
                            <div className="es-entry__head">
                              <span className="es-entry__title">{e.lesson?.subject?.name || '—'}</span>
                              <span className="es-entry__status" title={statusTitle}>
                                <Icon name={statusIcon} size={11} />
                              </span>
                            </div>
                            <div className="es-entry__meta">
                              {e.roomName || '—'}
                            </div>
                            <div className="es-entry__bottom">
                              <span className={'es-entry__type ' + (e.lesson?.type === 'group' ? 'is-group' : 'is-individual')}>
                                {e.lesson?.type === 'group' ? 'Группа' : 'Индив.'}
                              </span>
                              {e.studentsCount !== null && (
                                <span className="es-entry__count">
                                  <Icon name="users" size={10} /> {e.studentsCount}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                      {canCreate && items.length === 0 && (
                        <button
                          type="button"
                          className="es-cell__add"
                          onClick={() => setParams({ create: iso })}
                          title="Добавить занятие"
                        >
                          <Icon name="plus" size={11} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {params.open && (
        <TeacherExtraEntryModal
          entryId={Number(params.open)}
          onClose={() => { setParams({ open: '' }); reload(); }}
        />
      )}
      {params.create && (
        <TeacherExtraEntryCreateModal
          defaultDate={params.create}
          lessonCards={lessonCards}
          times={times}
          rooms={rooms}
          onClose={() => setParams({ create: '' })}
          onCreated={(id) => { setParams({ create: '', open: String(id) }); reload(); }}
        />
      )}
    </>
  );
}
